import { useState, useEffect, useRef } from 'react'
import PhoneLogin from './components/Auth/PhoneLogin'
import VerificationCode from './components/Auth/VerificationCode'
import DataView from './components/Data/DataView'
import ModulesConcept from './components/Modules/ModulesConcept'
import { LanguageProvider } from './context/LanguageContext'
import { SignOutConfirmProvider } from './components/Common/ConfirmSignOut'
import { projectClass } from './data/mockDataV2'
import { getClass, getFamily, isUsingMockData, startLogin } from './services/apiService'
import { initiateCustomAuth, newAttemptId, respondToAuthChallenge, revokeCurrentSession } from './services/cognitoAuth'
import { setSessionExpiredHandler } from './services/httpClient'
import { clearLegacyFamilyData, loadMockSession, loadSession, saveMockSession } from './services/tokenStore'
import { navigate } from './hooks/useRoute'

const VIEW_MODE_STORAGE_KEY = 'kidsParentsApp_viewMode'
const SEND_COOLDOWN_MS = 15 * 1000

const hasStoredSession = () => {
  if (isUsingMockData()) {
    return Boolean(loadMockSession()?.phoneNumber)
  }
  const session = loadSession()
  return Boolean(session?.accessToken || session?.refreshToken)
}

const clearViewMode = () => {
  try {
    localStorage.removeItem(VIEW_MODE_STORAGE_KEY)
  } catch (error) {
    console.warn('Failed to clear view mode:', error)
  }
}

// `error` is a translation key unless the API supplied its own message.
async function loadModulesAfterAuth(phoneForMock) {
  if (isUsingMockData()) {
    const familyResult = await getFamily(phoneForMock)
    return {
      ok: familyResult.success && Boolean(familyResult.data?.familyData),
      familyData: familyResult.data?.familyData || null,
      teacherClasses: [],
      error: familyResult.data?.error || 'auth.familyLoadFailed',
    }
  }

  const [familyResult, classResult] = await Promise.all([getFamily(), getClass()])
  if (!familyResult.success && !classResult.success) {
    return {
      ok: false,
      familyData: null,
      teacherClasses: [],
      error:
        familyResult.data?.error ||
        classResult.data?.error ||
        'auth.accountLoadFailed',
    }
  }

  const familyData = familyResult.success ? familyResult.data?.familyData || null : null
  const teacherClasses = classResult.success
    ? (classResult.data?.classes || []).map(projectClass)
    : []

  if (!familyData && teacherClasses.length === 0) {
    const hardError =
      (!familyResult.success && familyResult.data?.error) ||
      (!classResult.success && classResult.data?.error) ||
      null
    return {
      ok: false,
      familyData: null,
      teacherClasses: [],
      error: hardError || 'auth.noAccountData',
    }
  }

  return { ok: true, familyData, teacherClasses, error: null }
}

function App() {
  const [currentPage, setCurrentPage] = useState(() => (hasStoredSession() ? 'data' : 'login'))
  const [phoneNumber, setPhoneNumber] = useState('')
  const [verificationMethod, setVerificationMethod] = useState('')
  const [familyData, setFamilyData] = useState(null)
  const [teacherClasses, setTeacherClasses] = useState([])
  // App renders <LanguageProvider> itself, so it cannot call useTranslation.
  // Error state therefore holds translation KEYS ('auth.sendFailed'); the Auth
  // screens render them through tMessage(), which also passes through literal
  // server-supplied error text untouched.
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isInitializing, setIsInitializing] = useState(() => hasStoredSession())
  const [loadingPhase, setLoadingPhase] = useState('')
  const [cognitoChallengeSession, setCognitoChallengeSession] = useState(null)
  const [authAttemptId, setAuthAttemptId] = useState(null)
  const [sendCooldownUntil, setSendCooldownUntil] = useState(0)
  const loginInFlightRef = useRef(false)

  const resetToLogin = () => {
    setCurrentPage('login')
    setPhoneNumber('')
    setVerificationMethod('')
    setFamilyData(null)
    setTeacherClasses([])
    setError('')
    setIsLoading(false)
    setIsInitializing(false)
    setLoadingPhase('')
    setCognitoChallengeSession(null)
    setAuthAttemptId(null)
    clearViewMode()
    // Drop any module path so the next sign-in starts from the default page.
    navigate('/', { replace: true })
  }

  useEffect(() => {
    setSessionExpiredHandler(() => {
      resetToLogin()
    })
    return () => setSessionExpiredHandler(null)
  }, [])

  useEffect(() => {
    let cancelled = false

    const restore = async () => {
      clearLegacyFamilyData()
      if (!hasStoredSession()) {
        if (!cancelled) {
          setIsInitializing(false)
        }
        return
      }
      // Mock mode: the stored "session" is just the phone number used to log in.
      const mockPhone = isUsingMockData() ? loadMockSession()?.phoneNumber : undefined
      try {
        const loaded = await loadModulesAfterAuth(mockPhone)
        if (cancelled) return
        if (loaded.ok) {
          if (mockPhone) setPhoneNumber(mockPhone)
          setFamilyData(loaded.familyData)
          setTeacherClasses(loaded.teacherClasses)
          setCurrentPage('data')
        } else {
          await revokeCurrentSession()
          resetToLogin()
        }
      } catch (err) {
        console.warn('Session restore failed:', err)
        if (!cancelled) {
          await revokeCurrentSession()
          resetToLogin()
        }
      } finally {
        if (!cancelled) {
          setIsInitializing(false)
        }
      }
    }

    restore()
    return () => {
      cancelled = true
    }
  }, [])

  const handlePhoneSubmit = async (phone, method) => {
    if (loginInFlightRef.current || Date.now() < sendCooldownUntil) {
      setError('auth.waitBeforeResend')
      return
    }

    loginInFlightRef.current = true
    setIsLoading(true)
    setLoadingPhase('sending')
    setError('')
    const attemptId = newAttemptId()
    setAuthAttemptId(attemptId)
    console.info('auth_attempt_start', { attemptId, method })

    try {
      const result = await startLogin(phone, method)
      if (!result.success) {
        setError(result.data.error || 'auth.phoneNotFound')
        return
      }

      if (isUsingMockData()) {
        setPhoneNumber(phone)
        setVerificationMethod(method)
        setSendCooldownUntil(Date.now() + SEND_COOLDOWN_MS)
        setCurrentPage('verification')
        return
      }

      const challenge = await initiateCustomAuth(phone, method, attemptId)
      setPhoneNumber(phone)
      setVerificationMethod(method)
      setCognitoChallengeSession(challenge.session)
      setSendCooldownUntil(Date.now() + SEND_COOLDOWN_MS)
      setCurrentPage('verification')
    } catch (err) {
      console.error('Error starting login:', err, { attemptId })
      setError('auth.sendFailed')
    } finally {
      loginInFlightRef.current = false
      setIsLoading(false)
      setLoadingPhase('')
    }
  }

  const handleVerificationSubmit = async (code) => {
    setIsLoading(true)
    setLoadingPhase('verifying')
    setError('')

    try {
      if (isUsingMockData()) {
        if (code !== '123456') {
          setError('auth.invalidCodeTryAgain')
          return
        }
        setLoadingPhase('family')
        const loaded = await loadModulesAfterAuth(phoneNumber)
        if (!loaded.ok) {
          setError(loaded.error || 'auth.familyLoadFailed')
          return
        }
        saveMockSession({ phoneNumber })
        setFamilyData(loaded.familyData)
        setTeacherClasses(loaded.teacherClasses)
        setCurrentPage('data')
        return
      }

      const result = await respondToAuthChallenge(phoneNumber, cognitoChallengeSession, code, authAttemptId)
      console.info('auth_attempt_verify', { attemptId: authAttemptId, issuedTokens: Boolean(result.tokens) })
      if (result.tokens) {
        setCognitoChallengeSession(null)
        setLoadingPhase('family')
        const loaded = await loadModulesAfterAuth()
        if (!loaded.ok) {
          setError(loaded.error || 'auth.accountLoadFailed')
          return
        }
        setFamilyData(loaded.familyData)
        setTeacherClasses(loaded.teacherClasses)
        setCurrentPage('data')
        return
      }

      setCognitoChallengeSession(result.session)
      setError('auth.invalidCodeTryAgain')
    } catch (err) {
      console.error('Error verifying OTP:', err, { attemptId: authAttemptId })
      setCognitoChallengeSession(null)
      setCurrentPage('login')
      setError('auth.verifyFailed')
    } finally {
      setIsLoading(false)
      setLoadingPhase('')
    }
  }

  const handleBackToLogin = async () => {
    await revokeCurrentSession()
    resetToLogin()
  }

  const renderCurrentPage = () => {
    switch (currentPage) {
      case 'login':
        return (
          <PhoneLogin
            onSubmit={handlePhoneSubmit}
            error={error}
            isLoading={isLoading}
            sendCooldownUntil={sendCooldownUntil}
          />
        )
      case 'verification':
        return (
          <VerificationCode
            phoneNumber={phoneNumber}
            verificationMethod={verificationMethod}
            onSubmit={handleVerificationSubmit}
            onBack={handleBackToLogin}
            error={error}
            isLoading={isLoading}
            loadingPhase={loadingPhase}
          />
        )
      case 'data': {
        // Restoring a stored session: nothing about this login is known yet
        // (no family, no classes), so show the loading placeholder rather than
        // an empty page, and hold off mounting the modules shell - its nav
        // (and the URL it syncs to) depends on which modules the login has.
        if (isInitializing) {
          return (
            <DataView
              phoneNumber={phoneNumber}
              familyData={null}
              onBack={handleBackToLogin}
              isLoading
            />
          )
        }
        const hasFamily = Boolean(familyData)
        const familyView = hasFamily ? (
          <DataView phoneNumber={phoneNumber} familyData={familyData} onBack={handleBackToLogin} />
        ) : null
        const useModules = isUsingMockData() || hasFamily || teacherClasses.length > 0
        return useModules ? (
          <ModulesConcept
            phoneNumber={phoneNumber}
            familyView={familyView}
            hasFamily={hasFamily}
            teacherClasses={teacherClasses}
            onSignOut={handleBackToLogin}
          />
        ) : (
          familyView
        )
      }
      default:
        return (
          <PhoneLogin
            onSubmit={handlePhoneSubmit}
            error={error}
            isLoading={isLoading}
            sendCooldownUntil={sendCooldownUntil}
          />
        )
    }
  }

  return (
    <LanguageProvider>
      <SignOutConfirmProvider>
      <div className={`app ${currentPage === 'data' ? 'app-data-view' : 'app-auth-view'}`}>
        {renderCurrentPage()}
      </div>
      </SignOutConfirmProvider>
    </LanguageProvider>
  )
}

export default App
